/**
 * The live coverage figures: open roles, companies watched, countries, ATS
 * systems. One fetch, shared by everything on the page that states scale, so
 * the band under the hero and the sentence in the Radar card cannot disagree.
 */
import { api } from "@/lib/api";
import { useEffect, useState } from "react";

const KEYS = ["open_roles", "companies_watched", "countries", "ats_systems"];

let cached = null;
let inflight = null;

export function resetCoverageCache() {
  cached = null;
  inflight = null;
}

function valid(data) {
  return Boolean(data) && KEYS.every((k) => typeof data[k] === "number" && Number.isFinite(data[k]));
}

export function fetchCoverage() {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = Promise.resolve()
      .then(() => api.get("/public/coverage"))
      .then((res) => {
        const data = res && res.data;
        if (!valid(data)) throw new Error("coverage: unexpected shape");
        cached = data;
        return data;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** The figures, or null while loading and when they cannot be had. */
export function useCoverage() {
  const [data, setData] = useState(cached);
  useEffect(() => {
    let live = true;
    fetchCoverage()
      .then((d) => live && setData(d))
      .catch(() => live && setData(null));
    return () => {
      live = false;
    };
  }, []);
  return data;
}

/**
 * Round DOWN to something that stays true, and say that it is a floor.
 *
 *   1,312,456 → "1.3M+"     891,237 → "890,000+"     86 → "86"
 *
 * Down, not to nearest: "1.3M+" is true of 1,312,456 and of 1,399,999. To
 * nearest, 1,350,001 would read "1.4M+" and overstate. Zero and anything that
 * is not a positive number render nothing — a "0" on a landing page is a claim
 * of emptiness, and the callers hide the figure instead.
 */
export function formatCount(n) {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return null;
  const whole = Math.floor(n);
  // Under a hundred the exact figure is the honest one: "86 countries".
  if (whole < 100) return String(whole);
  // Two significant digits, floored: 891,237 → 890,000; 1,312,456 → 1,300,000.
  const unit = 10 ** (String(whole).length - 2);
  const floored = Math.floor(whole / unit) * unit;
  if (floored >= 1_000_000) {
    const m = floored / 1_000_000;
    return `${Number.isInteger(m) ? m : m.toFixed(1)}M+`;
  }
  return `${floored.toLocaleString("en-US")}+`;
}
